import { BuilderStore } from '@builder.io/react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProofDetails } from '../../app/api/proof/types';
import { stateNameToAbbreviation } from '../../lib/rest-service/state-abbreviations';
import './social-proof.scss';

export enum SocialProofContext {
  StickyBarContext = 'sticky-bar-context',
  StandaloneContext = 'standalone-context'
}

function getSocialProofMessage(socialProof: ProofDetails | null): string {
  if (!socialProof) return '';
  const { firstName, city, state, productName } = socialProof;
  const stateAbbr = stateNameToAbbreviation(state);
  return `${firstName} from ${city}, ${stateAbbr} just purchased ${productName}`;
}

function getTimeAgo(socialProof: ProofDetails | null): string {
  if (!socialProof) return '';

  const { timestamp } = socialProof;
  const now = new Date();
  const orderedAt = new Date(timestamp * 1000);
  const seconds = Math.floor((Number(now) - Number(orderedAt)) / 1000);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 }
  ];

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }

  return `${seconds} second${seconds > 1 ? 's' : ''} ago`;
}

export function SocialProof({
  builderState,
  socialProofs,
  offerId,
  context = SocialProofContext.StandaloneContext
}: {
  builderState: BuilderStore;
  socialProofs?: ProofDetails[];
  offerId?: number;
  context?: SocialProofContext;
}) {
  const productId = builderState.state?.product?.id ?? offerId;
  const [show, setShow] = useState<boolean>(false);
  const [proofs, setProofs] = useState<ProofDetails[]>([]);
  const [currentProof, setCurrentProof] = useState<ProofDetails | null>(null);
  const [timeAgoMessage, setTimeAgoMessage] = useState<string>('');
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [userDismissed, setUserDismissed] = useState<boolean>(false);
  const [proofBottom, setProofBottom] = useState<string>('0px');
  const [proofImageHeight, setProofImageHeight] = useState<number>(80);
  const [proofImageWidth, setProofImageWidth] = useState<number>(80);
  const [isStickyBar, setIsStickyBar] = useState<boolean>(false);
  const [transitionTime, setTransitionTime] = useState<number>(300);

  const handleNextProof = (allProofs: ProofDetails[], nextIndex: number) => {
    if (!allProofs.length) {
      return;
    }

    setCurrentIndex(nextIndex);
    const proof = allProofs[nextIndex] ?? null;
    setTimeAgoMessage(getTimeAgo(proof));
    setCurrentProof(proof);
    setShow(true);
  };

  const handleDismiss = () => {
    setShow(false);
    setUserDismissed(true);
  };

  const setInitialProofs = useCallback((proofs: ProofDetails[]) => {
    setProofs(proofs);
    handleNextProof(proofs, 0);
  }, []);

  useEffect(() => {
    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsStickyBar(e.matches && context === SocialProofContext.StickyBarContext);
    };

    const mediaQuery = window.matchMedia('(max-width: 991px)');
    setIsStickyBar(mediaQuery.matches && context === SocialProofContext.StickyBarContext);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, [context]);

  useEffect(() => {
    if (socialProofs?.length) {
      setInitialProofs(socialProofs);
    }
  }, [socialProofs, setInitialProofs]);

  useEffect(() => {
    if (!productId || socialProofs?.length) return;

    const fetchSocialProofs = async (): Promise<void> => {
      try {
        const proofResponse = await fetch(`/api/proof/${productId}`);
        const proofs = await proofResponse.json();
        setInitialProofs(proofs);
      } catch (error) {
        console.error('Failed to fetch social proofs:', error);
        setProofs([]);
      }
    };
    fetchSocialProofs().catch(() => setProofs([]));
  }, [setInitialProofs, productId, socialProofs]);

  useEffect(() => {
    const shouldShowNextProof = () => {
      if (!proofs.length) return false;
      if (isStickyBar) return true;
      if (userDismissed) return false;
      return true;
    };

    let timer: ReturnType<typeof setTimeout>;
    setTransitionTime(300);
    if (!isStickyBar && userDismissed) {
      setShow(false);
      setTransitionTime(0);
    } else if (show) {
      timer = setTimeout(() => {
        setShow(false);
      }, 8000);
    } else if (shouldShowNextProof()) {
      const nextIndex = (currentIndex + 1) % proofs.length;
      timer = setTimeout(
        () => {
          handleNextProof(proofs, nextIndex);
        },
        isStickyBar ? 400 : 4000
      );
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [show, userDismissed, currentIndex, proofs, isStickyBar]);

  useEffect(() => {
    // Observes if the Add To Cart sticky footer is initially visible and when its visibility changes
    // Adjusts the social proof notification position accordingly
    const pdpStickyBarSelector = '.product-sticky-bar';
    const funnelStickyBarSelector = '.sticky-cta';
    let isFunnel = false;

    let stickyBar = document.querySelector(pdpStickyBarSelector) as HTMLElement;
    if (!stickyBar) {
      isFunnel = true;
      stickyBar = document.querySelector(funnelStickyBarSelector) as HTMLElement;
    }

    if (!stickyBar) {
      return;
    }

    const isStickyBarVisible = (mutation: MutationRecord) => {
      if (isFunnel) {
        const { bottom } = (mutation.target as HTMLElement).style;
        return bottom === '0px';
      }
      const { visibility } = (mutation.target as HTMLElement).style;
      return visibility === 'visible';
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const height = stickyBar.offsetHeight;
          setProofBottom(isStickyBarVisible(mutation) ? `${height}px` : '0px');
          setProofImageHeight(80);
          setProofImageWidth(80);
        }
      });
    });

    observer.observe(stickyBar, { attributes: true, attributeFilter: ['style'] });
    const { visibility } = window.getComputedStyle(stickyBar);
    const initialVisibility = visibility !== 'hidden';
    setProofBottom(initialVisibility ? `60px` : '0px');
    setProofImageHeight(50);
    setProofImageWidth(50);
    return () => observer.disconnect();
  }, []);

  const transitionProps = useMemo(() => {
    if (isStickyBar) {
      return {
        enter: `transform ease-out duration-${transitionTime} transition`,
        enterFrom: 'translate-y-5 opacity-0 scale-95',
        enterTo: 'translate-y-0 opacity-100 scale-100',
        leave: `transform ease-in duration-${transitionTime} transition`,
        leaveFrom: 'translate-y-0 opacity-100 scale-100',
        leaveTo: '-translate-y-5 opacity-0 scale-95'
      };
    }

    return {
      enter: `transform ease-out duration-${transitionTime} transition`,
      enterFrom: 'translate-y-4 opacity-0',
      enterTo: 'translate-y-0 opacity-100',
      leave: `transform ease-in duration-${transitionTime} transition`,
      leaveFrom: 'translate-y-0 opacity-100',
      leaveTo: 'translate-y-4 opacity-0'
    };
  }, [isStickyBar, transitionTime]);

  return (
    <div
      aria-live="assertive"
      className="social-proof-container exterior-padding"
      style={{ bottom: proofBottom, transition: 'all 200ms ease-in-out' }}
    >
      <div className="social-proof-wrapper">
        <Transition show={show} {...transitionProps}>
          <div className="social-proof-card">
            <div className="social-proof-content interior-padding">
              <div className="social-proof-inner">
                {currentProof?.thumbnail && (
                  <div className="social-proof-image-container">
                    <Image
                      className="social-proof-image"
                      width={proofImageWidth}
                      height={proofImageHeight}
                      alt={'Social Proof Image'}
                      src={currentProof?.thumbnail ?? ''}
                    />
                  </div>
                )}
                <div className="social-proof-text">
                  <p className="social-proof-message truncated-text">
                    {getSocialProofMessage(currentProof)}
                  </p>
                  <div className="social-proof-time">
                    {timeAgoMessage}
                    <CheckCircleIcon className="social-proof-check-icon" aria-hidden="true" />
                  </div>
                </div>
                <div className="social-proof-close-container">
                  <button
                    type="button"
                    className="social-proof-close-button"
                    onClick={handleDismiss}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="social-proof-close-icon" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
}
